import type { ListBuilderConfiguration } from '../types';

/**
 * Opportunity Client Embedded list configuration
 * Shows opportunities embedded in a client view
 * Features kanban config for pipeline view
 * Converted from JSON to TypeScript for type safety
 */
export const OPPORTUNITY_CLIENT_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'client-embedded-opportunities',
			name: 'Client Opportunities',
			type: 'default',
			active: true,
			cacheTTL: 600000,
			columns: [
				{
					id: 'name',
					fields: [
						{
							id: 'opportunity-name',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'opportunity',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'mSmName', objectTable: 'opportunity' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'name-header', text: 'Opportunity' }],
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'mSmName',
							objectTable: 'opportunity',
						},
					],
				},
				{
					id: 'stage',
					fields: [
						{
							id: 'stage-name',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'mSmFkMSmClientOpportunityStage.mSmName',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'static',
									staticColor: { bg: '#3B82F6', text: '#FFFFFF' },
								},
							},
							displayStyle: 'badge',
						},
					],
					headers: [{ id: 'stage-header', text: 'Stage' }],
					sortable: true,
					alignment: 'center',
					sortFields: [{ direction: 'ASC', fieldName: 'stageName' }],
				},
				{
					id: 'amount',
					fields: [
						{
							id: 'opportunity-amount',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'mSmAmount', objectTable: 'opportunity' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
								formatOptions: {
									numberFormat: 'currency',
									decimalPlaces: 2,
									currencySymbol: '$',
									currencyPosition: 'prefix',
									thousandSeparator: true,
								},
							},
							displayStyle: 'currency',
						},
					],
					headers: [{ id: 'amount-header', text: 'Amount' }],
					sortable: true,
					alignment: 'right',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'mSmAmount',
							objectTable: 'opportunity',
						},
					],
				},
				{
					id: 'probability',
					fields: [
						{
							id: 'stage-probability',
							dataConfig: {
								type: 'template',
								template: '{mSmFkMSmClientOpportunityStage.mSmProbability}%',
								templateFields: [
									{
										fieldName: 'mSmFkMSmClientOpportunityStage.mSmProbability',
									},
								],
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'probability-header', text: 'Probability' }],
					sortable: true,
					alignment: 'center',
					sortFields: [{ direction: 'DESC', fieldName: 'stageProbability' }],
				},
				{
					id: 'close-date',
					fields: [
						{
							id: 'opportunity-close-date',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'mSmCloseDate',
									objectTable: 'opportunity',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { dateFormat: 'medium' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'close-date-header', text: 'Close Date' }],
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'mSmCloseDate',
							objectTable: 'opportunity',
						},
					],
				},
				{
					id: 'assigned-to',
					fields: [
						{
							id: 'user-name',
							dataConfig: {
								type: 'template',
								template: '{mSmFkUser.firstName} {mSmFkUser.lastName}',
								defaultValue: 'Unassigned',
								templateFields: [
									{ fieldName: 'mSmFkUser.firstName' },
									{ fieldName: 'mSmFkUser.lastName' },
								],
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'assigned-header', text: 'Assigned To' }],
					sortable: true,
					alignment: 'left',
					sortFields: [{ direction: 'ASC', fieldName: 'salesperson' }],
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'quick-view-action',
									label: 'Quick View',
									iconName: 'eye',
									callbackName: 'quickView',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'edit',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [{ id: 'actions-header', text: '' }],
					width: 120,
					align: 'center',
					sortable: false,
				},
			],
			viewMode: 'table',
			sortOrder: 0,
			kanbanConfig: {
				cardLayout: {
					sections: {
						body: [
							{
								id: 'card-close-date',
								label: 'Close',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'mSmCloseDate',
										objectTable: 'opportunity',
									},
								},
								styleConfig: {
									size: 'xs',
									colorRule: { type: 'default' },
									formatOptions: { dateFormat: 'short' },
								},
								displayStyle: 'date',
							},
							{
								id: 'card-user',
								dataConfig: {
									type: 'template',
									template: '{mSmFkUser.firstName} {mSmFkUser.lastName}',
									templateFields: [
										{ fieldName: 'mSmFkUser.firstName' },
										{ fieldName: 'mSmFkUser.lastName' },
									],
								},
								styleConfig: {
									size: 'xs',
									colorRule: { type: 'default' },
								},
								displayStyle: 'text',
							},
						],
						footer: [],
						header: [
							{
								id: 'card-name',
								link: {
									type: 'object',
									enabled: true,
									objectTable: 'opportunity',
								},
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'mSmName',
										objectTable: 'opportunity',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'default' },
									fontWeight: 'semibold',
								},
								displayStyle: 'text',
							},
							{
								id: 'card-amount',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'mSmAmount',
										objectTable: 'opportunity',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'default' },
									fontWeight: 'semibold',
									formatOptions: {
										numberFormat: 'currency',
										decimalPlaces: 0,
										currencySymbol: '$',
										currencyPosition: 'prefix',
										thousandSeparator: true,
									},
								},
								displayStyle: 'currency',
							},
						],
					},
				},
				groupByField: {
					fieldName: 'mSmFkMSmClientOpportunityStageId',
					objectTable: 'opportunity',
				},
				filterKey: 'stage',
				onColumnChange: 'onColumnChange',
				onCardClick: 'quickView',
				columnLabelField: { fieldName: 'mSmName', objectTable: 'stage' },
				columnOrderField: { fieldName: 'mSmOrdinal', objectTable: 'stage' },
				headerAggregations: [
					{ type: 'count', label: 'Count' },
					{
						type: 'sum',
						field: { fieldName: 'mSmAmount', objectTable: 'opportunity' },
						label: 'Total',
						format: {
							numberFormat: 'currency',
							decimalPlaces: 0,
							currencySymbol: '$',
							currencyPosition: 'prefix',
							thousandSeparator: true,
						},
					},
				],
				allowDragBetweenColumns: true,
			},
			defaultSortId: 'column-name-asc',
			requiredFields: [
				'id',
				'mSmName',
				'mSmAmount',
				'mSmCloseDate',
				'mSmOrdinal',
				'mSmFkClientId',
				'mSmFkMSmClientOpportunityStage.mSmName',
				'mSmFkMSmClientOpportunityStage.mSmProbability',
				'mSmFkUser.firstName',
				'mSmFkUser.lastName',
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [
			'stage',
			'opportunityUser',
			'opportunityDateRange',
			'opportunityAmount',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/opportunities/:id',
			objectLabel: 'Opportunity',
			objectTable: 'opportunity',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'm_sm_name', fieldLabel: 'Name' },
				{ fieldName: 'm_sm_amount', fieldLabel: 'Amount' },
				{ fieldName: 'm_sm_close_date', fieldLabel: 'Close Date' },
				{ fieldName: 'm_sm_stage_id', fieldLabel: 'Stage' },
				{ fieldName: 'm_sm_client_id', fieldLabel: 'Client' },
				{ fieldName: 'm_sm_user_id', fieldLabel: 'Assigned To' },
				{ fieldName: 'm_sm_created_at', fieldLabel: 'Created' },
				{ fieldName: 'm_sm_updated_at', fieldLabel: 'Updated' },
			],
		},
		{
			objectLabel: 'Stage',
			objectTable: 'stage',
			selectedFields: [
				{ fieldName: 'm_sm_name', fieldLabel: 'Stage Name' },
				{ fieldName: 'm_sm_probability', fieldLabel: 'Probability' },
			],
		},
		{
			objectLabel: 'User',
			objectTable: 'user',
			selectedFields: [
				{ fieldName: 'firstName', fieldLabel: 'First Name' },
				{ fieldName: 'lastName', fieldLabel: 'Last Name' },
			],
		},
	],
	tableSorts: [
		{
			id: 'close-date-asc',
			name: 'Close Date (Earliest First)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'm_sm_close_date',
					objectTable: 'opportunity',
				},
			],
		},
		{
			id: 'close-date-desc',
			name: 'Close Date (Latest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'm_sm_close_date',
					objectTable: 'opportunity',
				},
			],
		},
		{
			id: 'amount-desc',
			name: 'Amount (Highest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'm_sm_amount',
					objectTable: 'opportunity',
				},
			],
		},
		{
			id: 'created-desc',
			name: 'Recently Created',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'm_sm_created_at',
					objectTable: 'opportunity',
				},
			],
		},
	],
	importExport: {
		allowExport: true,
		allowImport: true,
		exportFields: [
			'm_sm_name',
			'm_sm_amount',
			'm_sm_close_date',
			'm_sm_stage_id',
		],
		importFields: ['m_sm_name', 'm_sm_amount', 'm_sm_close_date'],
	},
	showResetButton: true,
};
